import { api } from '../api';
import type { TicketVerweis } from '../testing/ticketverweise';

/**
 * Testing-Modul (docs/testing-tickets-plan.md): Tickets, dauerhaft auch live.
 *
 * Ist das Modul auf dem System nicht freigeschaltet, antwortet das
 * Backend mit 404 — die Oberfläche fragt deshalb zuerst den Status ab.
 */

export type TicketStatus =
  | 'neu'
  | 'triagiert'
  | 'in_arbeit'
  /** Erkannt, aber jetzt nicht dran. Zählt in keiner Kennzahl, die Arbeit misst. */
  | 'backlog'
  | 'auf_testsystem'
  | 'nachtest_ok'
  | 'erledigt'
  | 'abgelehnt'
  | 'duplikat';

export type TicketSeverity = 'blocker' | 'hoch' | 'mittel' | 'niedrig';
export type TicketKind =
  | 'bug'
  | 'idee'
  | 'frage'
  | 'feature_wunsch'
  | 'verbesserung'
  | 'test_fehlt';

/** Jüngster Verlaufseintrag — was zuletzt mit dem Ticket passiert ist. */
export interface TicketLastActionDto {
  type: string;
  description: string;
  actorName: string | null;
  createdAt: string | null;
}

export interface TicketDto {
  id: number;
  number: number;
  /** Anzeigeform der Nummer, z. B. „T-0013". */
  label: string;
  title: string;
  reportedTitle: string;
  kind: TicketKind;
  severity: TicketSeverity;
  area: string | null;
  /** Story aus dem Abnahme-Katalog, z. B. „PERS-3". */
  storyKey?: string | null;
  status: TicketStatus;
  route: string | null;
  duplicateOfId: number | null;
  reporterName: string | null;
  reporterRole: string | null;
  reporterUserId: number | null;
  standortId: number | null;
  assignedAccountId: string | null;
  assignedName: string | null;
  assignedAt: string | null;
  aiState: string;
  fixedCommit: string | null;
  fixedVersion: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  resolvedAt: string | null;
  /** Abgeschlossen und damit gesperrt — nur „wieder öffnen" geht noch. */
  closed?: boolean;
  /** Wohin der Status von hier aus darf; alles andere weist der Server ab. */
  allowedStatus?: TicketStatus[];
  /** Nur in der Liste: der jüngste Verlaufseintrag. */
  lastAction?: TicketLastActionDto;
}

export type { TicketVerweis };

export interface TicketHistoryDto {
  /** Konten, die der Eintrag erwähnt — für die Hervorhebung im Text. */
  mentions?: string[];
  id: number;
  type: string;
  description: string;
  details: string | null;
  actorName: string | null;
  createdAt: string | null;
  /** Bilder, die zu diesem Kommentar gehören — nur in der Detailansicht. */
  attachments?: TicketAttachmentDto[];
}

/**
 * Ticket mit Inhalt, aber ohne Verlauf — so antworten Anlegen und Ändern.
 *
 * Bewusst ein eigener Typ: Ein Patch mit dieser Antwort in den Zustand der
 * Detailseite zu schreiben, hat sie zum Absturz gebracht (T-0036, T-0038), weil
 * dort `history` gelesen wurde. Jetzt merkt das der Compiler.
 */
export interface TicketContentDto extends TicketDto {
  /** Die geltende Beschreibung — die Korrektur, falls es eine gibt. */
  body: string;
  /** Der unveränderte Wortlaut der Meldung. */
  reportedBody: string;
  /** Ob jemand die Beschreibung nachbearbeitet hat. */
  bodyEdited: boolean;
  aiSummary: string | null;
  aiSteps: string | null;
  context: Record<string, unknown>;
  /** Wohin der Status von hier aus darf — alles andere weist der Server ab. */
  allowedStatus: TicketStatus[];
}

export interface TicketDuplicateDto {
  id: number;
  number: number;
  label: string;
  title: string;
  status: TicketStatus;
  /** 0 bis 1 — wie ähnlich sich beide Texte sind. */
  score: number;
  /** Warum das vorgeschlagen wird, in Worten. */
  reason: string;
}

/** Vollständige Detailansicht: nur GET /tickets/{id} liefert das. */
export interface TicketDetailDto extends TicketContentDto {
  history: TicketHistoryDto[];
  duplicateCandidates: TicketDuplicateDto[];
}

export interface TicketAttachmentDto {
  id: number;
  contentType: string;
  sizeBytes: number | null;
  kind: string;
  /** Gesetzt, wenn das Bild zu einem Kommentar gehört statt zur Meldung. */
  historyEntryId?: number | null;
  uploadedAt: string | null;
  deleteAfter: string | null;
  /** Kurzlebige Leseadresse; der Speicher ist nicht öffentlich. */
  url?: string;
}

export interface TicketListResult {
  items: TicketDto[];
  total: number;
  offset: number;
  limit: number;
  /** Welche Werte es gibt und wie oft — für die Filter in den Spaltenköpfen. */
  facets: Record<string, Record<string, number>>;
}

export interface TicketCategoryDto {
  key: string;
  label: string;
}

/** Wem sich ein Ticket zuweisen lässt. */
export interface TicketAssigneeDto {
  accountId: string;
  name: string;
  email: string | null;
  /** Ob das die angemeldete Person selbst ist. */
  isMe?: boolean;
}

/** Spalten, nach denen sich sortieren lässt — dieselben Schlüssel kennt der Server. */
export type TicketSort =
  | 'number'
  | 'title'
  | 'status'
  | 'severity'
  | 'area'
  | 'kind'
  | 'reporter'
  | 'assignee'
  | 'created'
  | 'updated';

export interface TestingStatusDto {
  enabled: boolean;
  /** Welches System meldet — Test- und Livesystem haben getrennte Bestände. */
  instance?: 'test' | 'live';
  /** Präfix der Ticketnummern: `T` Testsystem, `L` live. */
  prefix?: string;
  status: TicketStatus[];
  severity: TicketSeverity[];
  kind: TicketKind[];
  categories: TicketCategoryDto[];
}

export interface TicketStatsDto {
  /** Zurückgestellt — eigene Zahl, nicht in `open` enthalten. */
  backlog?: number;
  byStatus: Record<string, number>;
  open: number;
  blocker: number;
}

export interface TicketCreateInput {
  title?: string;
  body: string;
  kind?: TicketKind;
  severity?: TicketSeverity;
  area?: string;
  standortId?: number | null;
  context?: Record<string, unknown>;
}

export interface TicketPatchInput {
  title?: string;
  /** Korrigierte Beschreibung; der gemeldete Wortlaut bleibt daneben stehen. */
  body?: string;
  status?: TicketStatus;
  severity?: TicketSeverity;
  kind?: TicketKind;
  area?: string | null;
  /** Story aus dem Abnahme-Katalog („PERS-3"); `null` löst die Verbindung. */
  storyKey?: string | null;
  /** Konten, die eine geänderte Beschreibung mit `@Name` erwähnt. */
  mentions?: string[];
  /** Konto-ID, `mir` für einen selbst, `null` entzieht die Zuständigkeit. */
  assignedAccountId?: string | null;
  duplicateOfId?: number | null;
  /** Für den Link in der Zuweisungs-Mail. */
  officeBaseUrl?: string;
  /** Beim Umstellen auf „auf Testsystem“ den Melder per Mail bitten (T-0224, Standard an). */
  melderBitten?: boolean;
}

export interface TicketFilter {
  /** Mehrwertig: leer heisst „alle". */
  status?: TicketStatus[];
  severity?: TicketSeverity[];
  kind?: TicketKind[];
  area?: string[];
  /** Konto-ID, `mir` für die eigene oder `ohne` für die unverteilten. */
  assigned?: string;
  q?: string;
  includeClosed?: boolean;
  sort?: TicketSort;
  direction?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export const testingApi = {
  /** Ob es das Modul auf diesem System gibt. Antwortet auch, wenn es aus ist. */
  status: () => api.get<TestingStatusDto>('/api/testing/status'),

  list: (filter: TicketFilter = {}) => {
    const params: Record<string, string> = {};
    if (filter.status?.length) params.status = filter.status.join(',');
    if (filter.severity?.length) params.severity = filter.severity.join(',');
    if (filter.kind?.length) params.kind = filter.kind.join(',');
    if (filter.area?.length) params.area = filter.area.join(',');
    if (filter.assigned) params.assigned = filter.assigned;
    if (filter.q?.trim()) params.q = filter.q.trim();
    if (filter.includeClosed) params.includeClosed = 'true';
    if (filter.sort) params.sort = filter.sort;
    if (filter.direction) params.direction = filter.direction;
    if (filter.limit) params.limit = String(filter.limit);
    if (filter.offset) params.offset = String(filter.offset);
    return api.get<TicketListResult>('/api/testing/tickets', { params });
  },

  get: (id: number) => api.get<TicketDetailDto>(`/api/testing/tickets/${id}`),

  create: (input: TicketCreateInput) => api.post<TicketContentDto>('/api/testing/tickets', input),

  patch: (id: number, input: TicketPatchInput) =>
    api.patch<TicketContentDto>(`/api/testing/tickets/${id}`, input),

  /** `attachmentIds` sind vorher hochgeladene Bilder, die dazugehören. */
  /**
   * `mentions` sind die Konten, die der Text mit `@Name` erwähnt — abgeleitet
   * aus dem Text (`lib/testing/erwaehnungen`), nicht beim Tippen gesammelt. Der
   * Server prüft nach, ob der Name dort auch steht.
   */
  comment: (
    id: number,
    text: string,
    attachmentIds: number[] = [],
    mentions: string[] = []
  ) =>
    api.post<TicketHistoryDto>(`/api/testing/tickets/${id}/comments`, {
      text,
      attachmentIds,
      mentions,
      // Für den Link in der Erwähnungs-Mail.
      officeBaseUrl: window.location.origin,
    }),

  /**
   * Titel, Stand und Auszug zu Ticketnummern, die in einem Text stehen.
   *
   * Ein Aufruf für alle Nummern einer Seite — ein Tooltip, der erst beim
   * Überfahren nachlädt, ist bei einem Verweis mitten im Satz nutzlos.
   */
  refs: (numbers: number[]) =>
    api.post<{ items: TicketVerweis[] }>('/api/testing/tickets/refs', { numbers }),

  stats: () => api.get<TicketStatsDto>('/api/testing/stats'),

  /** Braucht `tickets_bearbeiten`; ohne das Recht antwortet der Server mit 403. */
  assignees: () => api.get<{ items: TicketAssigneeDto[] }>('/api/testing/assignees'),

  attachments: (id: number) =>
    api.get<{ items: TicketAttachmentDto[] }>(`/api/testing/tickets/${id}/attachments`),

  /**
   * Screenshot anhängen: Platz anfordern, direkt zu S3 hochladen, bestätigen.
   *
   * Der Upload geht am Backend vorbei — dasselbe Muster wie bei den CMS-Medien.
   * Schlägt er fehl, bleibt nur eine unbestätigte Zeile zurück, die der
   * Aufräumlauf später einsammelt; das Ticket selbst ist davon unberührt.
   */
  uploadScreenshot: async (ticketId: number, datei: Blob): Promise<TicketAttachmentDto> => {
    const slot = await api.post<{ attachmentId: number; uploadUrl: string; contentType: string }>(
      `/api/testing/tickets/${ticketId}/attachments/presign`,
      { contentType: datei.type, sizeBytes: datei.size },
    );
    const antwort = await fetch(slot.uploadUrl, {
      method: 'PUT',
      body: datei,
      headers: { 'Content-Type': slot.contentType },
    });
    if (!antwort.ok) throw new Error('Der Screenshot konnte nicht hochgeladen werden.');
    return api.post<TicketAttachmentDto>(
      `/api/testing/tickets/${ticketId}/attachments/${slot.attachmentId}/complete`,
      {},
    );
  },
};

/** Was als Screenshot durchgeht — dieselbe Liste wie im Backend. */
export const SCREENSHOT_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
export const SCREENSHOT_MAX_BYTES = 8 * 1024 * 1024;

export const TICKET_STATUS_LABEL: Record<TicketStatus, string> = {
  neu: 'Neu',
  triagiert: 'Triagiert',
  in_arbeit: 'In Arbeit',
  backlog: 'Backlog',
  // Der Schlüssel stammt vom Testsystem; live heisst derselbe Stand „ausgerollt".
  auf_testsystem: 'Bereit zum Nachtest',
  nachtest_ok: 'Nachtest in Ordnung',
  erledigt: 'Erledigt',
  abgelehnt: 'Abgelehnt',
  duplikat: 'Dublette',
};

export const TICKET_SEVERITY_LABEL: Record<TicketSeverity, string> = {
  blocker: 'Blocker',
  hoch: 'Hoch',
  mittel: 'Mittel',
  niedrig: 'Niedrig',
};

export const TICKET_KIND_LABEL: Record<TicketKind, string> = {
  bug: 'Fehler',
  idee: 'Idee',
  frage: 'Frage',
  feature_wunsch: 'Feature-Wunsch',
  verbesserung: 'Verbesserung',
  test_fehlt: 'Test fehlt',
};

/**
 * Was beim Melden zur Wahl steht. „Test fehlt“ legt die Abnahme für Kriterien ohne
 * belegenden Test an (docs/abnahme-plan.md, Abschnitt 12) — im Ticket selbst lässt
 * sich jede Art einstellen.
 */
export const TICKET_KIND_MELDEN: readonly TicketKind[] = [
  'bug',
  'verbesserung',
  'feature_wunsch',
  'idee',
  'frage',
];

/** Status, die als abgeschlossen gelten — die Liste blendet sie vorbelegt aus. */
export const TICKET_STATUS_CLOSED: readonly TicketStatus[] = ['erledigt', 'abgelehnt', 'duplikat'];
